/**
 * Starter catalog agent tools (starter-registry plan PR-L) — the agent
 * face of the catalog. "Make me a SWOT" resolves to our curated starter
 * instead of free-generation: `list_starters` reads the registry,
 * `start_from_starter` constructs the same StartIntent URL the
 * dashboard would and opens it. Both are READERS — no new content store.
 */

import type { IAgentTool } from '../IAgentTool';
import { listStarters, getStarter } from '../../../starters/StarterRegistry';
import { registerDefaultStarters } from '../../../starters/registerDefaultStarters';
import { revenueLineOf, type FunctionTag } from '../../../starters/Starter';
import { startIntentOf } from '../../../starters/startIntentOf';
import { serializeStartIntent } from '../../../seeds/startIntent';

const FUNCTION_TAGS: ReadonlyArray<string> = [
  'operations', 'people', 'it-cloud', 'risk-compliance',
  'clinical', 'strategy', 'projects', 'software', 'general',
];

const starterSummary = (id: string) => {
  const s = getStarter(id);
  if (!s) return null;
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    function: s.function,
    job: s.job,
    ...(s.trigger ? { trigger: s.trigger } : {}),
    artifactKind: s.artifact.kind,
    diagramType: s.artifact.kind === 'legacy' ? undefined : s.artifact.diagramType,
    revenueLine: revenueLineOf(s),
  };
};

export const listStartersTool: IAgentTool = {
  name: 'list_starters',
  description:
    'List the curated starter catalog (SWOT, kanban, org chart, audit readiness, swimlanes, …). ' +
    'Use before generating a diagram from scratch — when a starter matches the ask, prefer ' +
    'start_from_starter (purpose-built content beats generation). Filter by function area or free text.',
  parameters: {
    type: 'object',
    properties: {
      function: {
        type: 'string',
        enum: [...FUNCTION_TAGS],
        description: 'Function section to filter by (whose work this is).',
      },
      query: { type: 'string', description: 'Free-text match against name/description.' },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params) {
    registerDefaultStarters();
    const fn = typeof params.function === 'string' && FUNCTION_TAGS.includes(params.function)
      ? (params.function as FunctionTag)
      : undefined;
    const query = typeof params.query === 'string' ? params.query.trim().toLowerCase() : '';
    const starters = listStarters(fn ? { function: fn } : undefined)
      .filter((s) => s.artifact.kind !== 'legacy')
      .filter(
        (s) =>
          !query ||
          s.name.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query) ||
          s.id.includes(query),
      )
      .map((s) => starterSummary(s.id))
      .filter(Boolean);
    return {
      success: true,
      summary: `${starters.length} starter${starters.length === 1 ? '' : 's'} available${fn ? ` in ${fn}` : ''}.`,
      data: { starters },
    };
  },
};

export const startFromStarterTool: IAgentTool = {
  name: 'start_from_starter',
  description:
    'Open a new diagram seeded from a catalog starter (use list_starters first to find ids). ' +
    'Creates the diagram in the current folder and opens it in a new tab.',
  parameters: {
    type: 'object',
    properties: {
      starterId: { type: 'string', description: 'Starter id from list_starters.' },
    },
    required: ['starterId'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params) {
    registerDefaultStarters();
    const starter = getStarter(String(params.starterId ?? ''));
    if (!starter) {
      return { success: false, summary: `Unknown starter '${String(params.starterId)}'. Use list_starters.` };
    }
    const intent = startIntentOf(starter, `agent-tool:${starter.id}`);
    if (!intent) {
      return { success: false, summary: `Starter '${starter.id}' is a legacy template — open it from the dashboard catalog.` };
    }
    // The editor URL is /f/:folderId/d/:diagramId — reuse the folder.
    const match = window.location.pathname.match(/\/f\/([^/]+)\//);
    if (!match) {
      return { success: false, summary: 'Could not resolve the current folder from the URL.' };
    }
    const search = new URLSearchParams(serializeStartIntent(intent));
    const href = `/f/${match[1]}/d/new?${search.toString()}`;
    window.open(href, '_blank', 'noopener,noreferrer');
    return {
      success: true,
      summary: `Opened a new ${starter.name} in this folder (new tab).`,
      data: { starterId: starter.id, href },
    };
  },
};
